import { findNestCandidate, type Point, type RowRect } from './nestTarget'

/**
 * **Umsortieren nach dem Zeiger, mit ruhender Mitte** (#204).
 *
 * dnd-kit tauscht an der Zeilenmitte: Einen Pixel darüber rückt die Zeile weg, einen
 * darunter rückt sie zurück. Eine Mitte, auf der man zum Einhängen verweilen könnte, gibt
 * es so nicht (gemeldet 27.09.2026). Hier gilt stattdessen:
 *
 * - oberes Viertel einer Zeile → die Lücke geht darüber auf,
 * - unteres Viertel → darunter,
 * - Mitte → nichts bewegt sich, solange die Lücke schon neben dieser Zeile liegt. Liegt sie
 *   woanders (der Zeiger hat die Ränder übersprungen), zählt die nähere Hälfte.
 * - zwischen zwei Zeilen (in der Lücke) → nichts bewegt sich.
 *
 * Zurück kommt das `over` für dnd-kit: Die Gezogene landet an dessen Platz in `order`
 * (`arrayMove`). Ist es die Gezogene selbst, bleibt alles, wie es ist.
 *
 * @param order  die sortierbaren Kennungen dieses Abschnitts, die Gezogene eingeschlossen
 * @param rows   die sichtbaren Rechtecke der übrigen Zeilen (mit ihrer Verschiebung)
 */
export function pointerSortOver({
  pointer, rows, order, activeId, currentOverId,
}: {
  pointer: Point
  rows: readonly RowRect[]
  order: readonly string[]
  activeId: string
  currentOverId: string | null
}): string {
  const a = order.indexOf(activeId)
  const current = currentOverId && order.includes(currentOverId) ? currentOverId : activeId
  const hit = findNestCandidate(pointer, rows.map(r => ({ ...r, left: -Infinity, right: Infinity })))
  if (a === -1 || !hit || !order.includes(hit.id)) return current

  const row = rows.find(r => r.id === hit.id)!
  if (hit.middle) {
    const arranged = [...order]
    arranged.splice(a, 1)
    arranged.splice(order.indexOf(current), 0, activeId)
    const at = arranged.indexOf(activeId)
    if (arranged[at - 1] === hit.id || arranged[at + 1] === hit.id) return current
  }
  const before = pointer.y < row.top + (row.bottom - row.top) / 2

  const without = order.filter(id => id !== activeId)
  const r = without.indexOf(hit.id)
  return order[before ? r : r + 1]!
}
